import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape, injectIntl } from 'react-intl';

// Helpers
import { mobile as mobileCheck } from 'dibs-client-check';
import { getFolderLink, getInvoiceLink, types, typesRequiringName } from '../utils/constants';
import { checkboxLabel, HeaderTitle, SubmitLabel } from '../messages/shareModalMessages';

import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { Checkbox } from 'dibs-elements/exports/Checkbox';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { Input } from 'dibs-elements/exports/Input';
import { TextArea } from 'dibs-elements/exports/TextArea';

import ValidatedInput from 'dibs-validated-input';
import ItemPreviews from './ItemPreviews';
import CopyToClipboard from './CopyToClipboard';
import Submit from './Submit';

// Styles
import styles from '../styles/shareModal.scss';

// These are placeholders for Inputs, Textarea
const placeholders = defineMessages({
    emails: {
        id: 'shareModal.placeholder.emails',
        defaultMessage: 'Enter recipient emails, separated by commas',
    },
    emailsMobile: {
        id: 'shareModal.placeholder.emails_mobile',
        defaultMessage: 'Enter recipient emails',
    },
    name: {
        id: 'shareModal.placeholder.name',
        defaultMessage: 'Enter your name',
    },
    message: {
        id: 'shareModal.placeholder.message',
        defaultMessage: 'Add a personalized message (optional)',
    },
    senderEmail: {
        id: 'shareModal.placeholder.sender_email',
        defaultMessage: 'Enter your email',
    },
});

const defaultCatalogTexts = defineMessages({
    message: {
        id: 'shareModal.defaultTexts.catalog.message',
        defaultMessage:
            'I want to share something beautiful with you! You can discover more at 1stdibs.com.',
    },
});

export const inputLabels = defineMessages({
    message: {
        id: 'shareModal.label.message',
        defaultMessage: 'Include a Message',
    },
});

const textAreaRows = {
    [types.CATALOG]: 4,
};

export class ShareModal extends React.Component {
    static defaultProps = {
        senderEmail: '',
        senderName: '',
        portfolioIds: [],
        recipientEmails: [],
        selectedItems: [],
        selectedItemImages: [],
        selectedItemTitles: [],

        onClickCopyLink: () => {},
        onShare: () => {},
    };

    constructor(props) {
        super(props);
        const { intl } = props;

        this.state = {
            validEmails: false,
            validYourEmail: false,
            isSending: false,
            emailForm: {
                message:
                    this.props.personalizedMessage ||
                    this.getDefaultText({ intl, fieldName: 'message' }),
                senderName: this.props.senderName,
                senderEmail: this.props.senderEmail,
                recipientEmails: this.props.recipientEmails,
                showNotes: false,
            },
        };

        this.setStateWrapper = this.setStateWrapper.bind(this);
        this.updateFormValue = this.updateFormValue.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.getDefaultText = this.getDefaultText.bind(this);
    }

    componentWillReceiveProps(newProps) {
        const { senderEmail } = newProps;
        if (senderEmail !== this.state.emailForm.senderEmail) {
            this.setState({
                emailForm: {
                    ...this.state.emailForm,
                    senderEmail,
                },
            });
        }
    }

    setStateWrapper(name, value) {
        return () => this.setState({ [name]: value });
    }

    updateFormValue(formFieldName) {
        return e => {
            let updatedValue;

            switch (formFieldName) {
                case 'recipientEmails':
                    updatedValue = e.target.value.split(',');
                    break;
                case 'showNotes':
                    updatedValue = !this.state.showNotes;
                    break;
                default:
                    // 'senderName', 'senderEmail', 'message'
                    updatedValue = e.target.value;
                    break;
            }

            const emailForm = { ...this.state.emailForm, [formFieldName]: updatedValue };
            this.setState({ emailForm });
        };
    }

    onSubmit() {
        const { portfolioIds, type } = this.props;

        const {
            emailForm: { message, senderEmail, senderName, showNotes, recipientEmails },
        } = this.state;

        let inputs = {
            message,
            recipientEmails,
            senderEmail,
            senderName,
        };

        if (type === types.FOLDERS) {
            inputs = {
                ...inputs,
                portfolioIds,
                shareType: types.FOLDERS,
            };
        } else if (type === types.INVOICE) {
            inputs = {
                ...inputs,
                shareType: types.INVOICE,
                transactionId: this.props.transactionId,
            };
        } else if (type === types.PDP) {
            inputs = {
                ...inputs,
                itemId: this.props.selectedItems[0].itemId,
                shareType: types.PDP,
            };
        } else if (type === types.DESIGN_PROFILE) {
            inputs = {
                ...inputs,
                shareType: types.DESIGN_PROFILE,
            };
        } else if (type === types.CATALOG) {
            inputs = {
                ...inputs,
                catalogIds: this.props.catalogIds,
                shareType: types.CATALOG,
            };
        } else {
            inputs = {
                ...inputs,
                portfolioItemIds: this.props.selectedItems.map(item => item.portfolioItemId),
                shareType: types.ITEMS,
                showNotes,
            };
        }

        this.setState({ isSending: true });
        this.props.onShare({ ...inputs });
    }

    getDefaultText({ intl, fieldName }) {
        const { type } = this.props;

        switch (type) {
            case types.CATALOG: {
                return intl.formatMessage(defaultCatalogTexts[fieldName]) || '';
            }
            default: {
                return '';
            }
        }
    }

    render() {
        const { intl, type } = this.props;
        const {
            emailForm: { message, senderEmail, senderName, showNotes, recipientEmails },
            isSending,
        } = this.state;
        const portfolioName = this.props.portfolioName || '';
        const portfolioId =
            this.props.portfolioIds && this.props.portfolioIds.length
                ? this.props.portfolioIds[0]
                : '';
        const folderUrl = getFolderLink(portfolioName, portfolioId);
        const invoiceUrl = getInvoiceLink(this.props.transactionId, this.props.invoiceAccessToken);
        const headerTitle = this.props.title || <HeaderTitle type={type} />;
        const submitLabel = <SubmitLabel type={type} />;
        const recipientEmailsPlaceholder = mobileCheck.any() ? 'emailsMobile' : 'emails';
        const recipientEmailsValue = recipientEmails ? recipientEmails.join(',') : '';
        const hasValidSenderName = !(!senderName.trim() && typesRequiringName.includes(type));
        const hasValidSenderEmail = senderEmail.length && this.state.validYourEmail;
        let isSubmitDisabled =
            !hasValidSenderName || !recipientEmailsValue.length || !this.state.validEmails;

        if (type === types.PDP) {
            isSubmitDisabled = isSubmitDisabled || !hasValidSenderEmail;
        } else if (type === types.DESIGN_PROFILE) {
            isSubmitDisabled = !hasValidSenderName || !hasValidSenderEmail;
        }

        let textAreaLabel;
        if (type === types.CATALOG) {
            textAreaLabel = intl.formatMessage(inputLabels.message);
        }

        return (
            <ModalContainer
                dataTn="share-modal"
                onClose={this.props.closeModal}
                isOpen={this.props.modalOpen}
                modalPosition="center"
            >
                <ModalCloseButton onClick={this.props.closeModal} />
                <BarHeader title={headerTitle} />
                <ModalBody>
                    {this.props.selectedItemImages.length &&
                    (type !== types.INVOICE && type !== types.DESIGN_PROFILE) ? (
                        <div className={styles.preview}>
                            <ItemPreviews
                                styleType={type}
                                itemImages={this.props.selectedItemImages}
                                itemTitles={this.props.selectedItemTitles}
                            />
                        </div>
                    ) : null}
                    {type === types.INVOICE && (
                        <div className={styles.invoiceBody}>
                            <FormattedMessage
                                defaultMessage="Share this invoice to let others view your customized purchasing details."
                                id="shareModal.INVOICE.bodyText"
                            />
                            {this.props.isVerifiedTrade && (
                                <span className={styles.verifiedTradeInvoice}>
                                    <FormattedMessage
                                        defaultMessage="Trade members will be able to transfer any custom offers to their account to complete the purchase on your behalf."
                                        id="shareModal.INVOICE.bodyText.trade"
                                    />
                                </span>
                            )}
                        </div>
                    )}
                    {type === types.ITEMS && (
                        <Checkbox
                            name="share-portfolio-modal-notes-checkbox"
                            label={checkboxLabel}
                            labelClass={styles.shareNotesLabel}
                            wrapperClass={styles.shareNotes}
                            checked={showNotes}
                            dataTn="share-modal-notes-checkbox"
                            onChange={this.updateFormValue('showNotes')}
                            disabled={isSending}
                        />
                    )}
                    {type === types.INVOICE ? null : (
                        <div className={styles.input}>
                            <Input
                                dataTn="share-modal-name"
                                hasAnimatedPlaceholder
                                maxLength={100}
                                onChange={this.updateFormValue('senderName')}
                                placeholder={intl.formatMessage(placeholders.name)}
                                value={senderName}
                                disabled={isSending}
                            />
                        </div>
                    )}
                    {(type === types.CATALOG ||
                        type === types.PDP ||
                        type === types.DESIGN_PROFILE) && (
                        <div className={styles.input} data-tn="share-modal-sender-email">
                            <ValidatedInput
                                dataTn="share-modal-email"
                                hasAnimatedPlaceholder
                                showSuccessIcon
                                onChange={this.updateFormValue('senderEmail')}
                                onError={this.setStateWrapper('validYourEmail', false)}
                                onSuccess={this.setStateWrapper('validYourEmail', true)}
                                placeholder={intl.formatMessage(placeholders.senderEmail)}
                                validationType="email"
                                value={senderEmail}
                                validateOnMount={!!senderEmail}
                                disabled={isSending}
                            />
                        </div>
                    )}
                    {type !== types.DESIGN_PROFILE && (
                        <div className={styles.input} data-tn="share-modal-recipient-email">
                            <ValidatedInput
                                dataTn="share-modal-email"
                                hasAnimatedPlaceholder
                                showSuccessIcon
                                onChange={this.updateFormValue('recipientEmails')}
                                onError={this.setStateWrapper('validEmails', false)}
                                onSuccess={this.setStateWrapper('validEmails', true)}
                                placeholder={intl.formatMessage(
                                    placeholders[recipientEmailsPlaceholder]
                                )}
                                validationType="emails"
                                value={recipientEmailsValue}
                                disabled={isSending}
                            />
                        </div>
                    )}
                    <div className={styles.textArea}>
                        <TextArea
                            dataTn="share-modal-message"
                            rows={textAreaRows[type]}
                            label={textAreaLabel}
                            maxLength={500}
                            onChange={this.updateFormValue('message')}
                            placeholder={intl.formatMessage(placeholders.message)}
                            isMobile
                            value={message}
                            disabled={isSending}
                        />
                    </div>
                    <Submit
                        label={submitLabel}
                        isSending={isSending}
                        disabled={isSubmitDisabled}
                        onClick={this.onSubmit}
                    />
                    {type === types.INVOICE ? null : (
                        <div className={styles.shareInfo}>
                            <FormattedMessage
                                id="shareModal.footer"
                                defaultMessage="You will receive a copy of this email."
                            />
                        </div>
                    )}
                </ModalBody>
                {type === types.FOLDERS && (
                    <CopyToClipboard
                        url={folderUrl}
                        type={type}
                        onClickCopyLink={this.props.onClickCopyLink}
                    />
                )}
                {type === types.INVOICE && (
                    <CopyToClipboard
                        url={invoiceUrl}
                        type={type}
                        onClickCopyLink={this.props.onClickCopyLink}
                    />
                )}
            </ModalContainer>
        );
    }
}

ShareModal.propTypes = {
    catalogIds: PropTypes.shape({
        rootId: PropTypes.string,
        pageTypeId: PropTypes.string,
    }),
    item: PropTypes.object,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    personalizedMessage: PropTypes.string,
    portfolioName: PropTypes.string,
    senderName: PropTypes.string,
    senderEmail: PropTypes.string,
    transactionId: PropTypes.string,
    invoiceAccessToken: PropTypes.string,
    isVerifiedTrade: PropTypes.bool,
    onClickCopyLink: PropTypes.func,

    type: PropTypes.oneOf([
        types.CATALOG,
        types.FOLDERS,
        types.ITEMS,
        types.INVOICE,
        types.PDP,
        types.DESIGN_PROFILE,
    ]),
    portfolioIds: PropTypes.array,
    recipientEmails: PropTypes.array,
    selectedItems: PropTypes.array,
    selectedItemImages: PropTypes.array,
    selectedItemTitles: PropTypes.array,

    modalOpen: PropTypes.bool,

    closeModal: PropTypes.func,
    onShare: PropTypes.func,

    // see: https://github.com/yahoo/react-intl/wiki/API#injectintl
    intl: intlShape.isRequired,
};

export default injectIntl(ShareModal);
